import type { ThemeDto, ThemePaletteDto } from '@api/types/theme'
import type { ThemeConfig } from './types'
import { typography, breakpoints, components } from './base.theme'
import { ThemeOptions, PaletteOptions } from '@mui/material/styles'

/** Convert an API palette DTO into MUI ThemeOptions */
function buildPalette(palette: ThemePaletteDto, mode: 'light' | 'dark'): ThemeOptions {
  return {
    palette: {
      mode,
      primary: {
        main: palette.primary,
        light: palette.primaryLight,
        dark: palette.primaryDark,
        contrastText: palette.primaryContrast,
      },
      secondary: {
        main: palette.secondary,
        light: palette.secondaryLight,
        dark: palette.secondaryDark,
        contrastText: palette.secondaryContrast,
      },
      background: {
        default: palette.backgroundDefault,
        paper: palette.backgroundPaper,
      },
      text: {
        primary: palette.textPrimary,
        secondary: palette.textSecondary,
      },
    },
    typography,
    breakpoints,
    components,
  }
}

/**
 * Rebuild a ThemeConfig from cached light/dark palettes (see uiStore's ThemeCache). Used to
 * seed the registry synchronously on first load — before the server themes fetch resolves — so
 * the selected theme paints immediately instead of flashing the default. The static
 * typography/breakpoints/components are re-attached here since only the palette is persisted.
 */
export function themeConfigFromPalettes(
  id: string,
  light: PaletteOptions,
  dark: PaletteOptions
): ThemeConfig {
  return {
    id,
    name: id,
    description: '',
    source: 'server',
    slug: id,
    light: { palette: light, typography, breakpoints, components },
    dark: { palette: dark, typography, breakpoints, components },
  }
}

/** Convert a ThemeDto from the API into a full ThemeConfig for the registry */
export function buildThemeConfig(dto: ThemeDto): ThemeConfig {
  return {
    id: dto.slug,
    name: dto.name,
    description: dto.description,
    source: 'server',
    slug: dto.slug,
    preview: {
      primaryColor: dto.previewPrimary,
      secondaryColor: dto.previewSecondary,
    },
    light: buildPalette(dto.light, 'light'),
    dark: buildPalette(dto.dark, 'dark'),
  }
}
